/**
 * Versiegeschiedenis (US-035).
 *
 * De lijsten staan in de gedeelde code (`packages/shared/src/versies.ts`) en niet in de database.
 * Er is dus geen verzoek nodig om dit scherm te vullen, en het kan niet gebeuren dat het nummer
 * linksonder in het menu iets anders zegt dan wat hier staat.
 *
 * Bovenaan staat wat er nu draait, want dat is de vraag waarmee je hier komt: "draait mijn
 * wijziging al?". Het antwoord is de commit: een release verandert zelden, een commit zegt precies
 * welke code er staat. Daaronder volgen de officiële releases, nieuwste eerst, en ingeklapt het
 * oude logboek per commit uit de tijd vóór de eerste release.
 */

import {
  formatDateNl,
  RELEASES,
  releaseTag,
  VOORGESCHIEDENIS,
  type OntwikkelVersie,
  type Release,
} from '@prijsmonitor/shared';

import { ui } from '../components/ui/index.js';
import { useSession } from '../session/SessionContext.js';
import styles from './VersiesScreen.module.css';

function Punten({ punten }: { punten: string[] }): React.JSX.Element {
  return (
    <ul className={styles.punten}>
      {punten.map((punt) => (
        <li key={punt}>{punt}</li>
      ))}
    </ul>
  );
}

function ReleaseItem({
  release,
  draaitNu,
}: {
  release: Release;
  draaitNu: boolean;
}): React.JSX.Element {
  return (
    <li className={draaitNu ? styles.itemHuidig : styles.item}>
      <div className={styles.kop}>
        <span className={styles.nummer}>{release.versie}</span>
        <span className={styles.datum}>{formatDateNl(release.datum)}</span>
        {draaitNu && <span className={styles.nu}>draait nu</span>}
        {/* De Git-tag is het enige dat deze release verbindt met één commit en één GitHub Release. */}
        <span className={styles.commit} title="Git-tag van deze release">
          {releaseTag(release.versie)}
        </span>
      </div>
      <div className={styles.titel}>{release.titel}</div>
      <Punten punten={release.punten} />
    </li>
  );
}

function OntwikkelItem({ versie }: { versie: OntwikkelVersie }): React.JSX.Element {
  return (
    <li className={styles.item}>
      <div className={styles.kop}>
        <span className={styles.nummer}>{versie.versie}</span>
        <span className={styles.datum}>{formatDateNl(versie.datum)}</span>
        {/*
          De commit-hash staat er klein bij. Voor de meeste lezers ruis, maar het is het enige dat
          een regel hier verbindt met wat er precies in de code is gebeurd.
        */}
        <span className={styles.commit} title="Commit in de broncode">
          {versie.commit}
        </span>
      </div>
      <div className={styles.titel}>{versie.titel}</div>
      <Punten punten={versie.punten} />
    </li>
  );
}

export function VersiesScreen(): React.JSX.Element {
  const { session } = useSession();
  const versie = session?.versie;
  const commit = session?.commit;
  const bekend = versie !== undefined && versie !== 'onbekend';
  const isRelease = bekend && RELEASES.some((release) => release.versie === versie);
  const eerste = VOORGESCHIEDENIS[VOORGESCHIEDENIS.length - 1];
  const laatste = VOORGESCHIEDENIS[0];

  return (
    <div className={ui.page}>
      <div className={ui.cardPadded}>
        <h2 className={ui.cardTitle}>Versiegeschiedenis</h2>
        <p className={ui.cardSub}>
          {bekend
            ? `Hier draait ${isRelease ? 'versie' : 'ontwikkelversie'} ${versie}`
            : 'De versie van deze omgeving is niet bekend'}
          {commit && commit !== 'onbekend' ? `, commit ${commit}.` : '.'}
        </p>
        {RELEASES.length === 0 && (
          <p className={ui.cardSub}>
            Er is nog geen officiële release. Het eerste versienummer (1.0.0) verschijnt zodra de
            testomgeving is geaccepteerd en naar productie gaat. Welke code er nu draait, zie je aan
            de commit.
          </p>
        )}
      </div>

      {RELEASES.length > 0 && (
        <ol className={styles.lijst}>
          {RELEASES.map((release) => (
            <ReleaseItem
              key={release.versie}
              release={release}
              draaitNu={isRelease && release.versie === versie}
            />
          ))}
        </ol>
      )}

      {/*
        Het oude logboek per commit. Ingeklapt: het is afgesloten en de meeste bezoekers komen voor
        wat er nu draait, maar het is de enige beschrijving van wat er in die tijd is veranderd.
      */}
      <details className={styles.historie}>
        <summary>
          Ontwikkelhistorie vóór de eerste release ({eerste?.versie} – {laatste?.versie})
        </summary>
        <ol className={styles.lijst}>
          {VOORGESCHIEDENIS.map((item) => (
            <OntwikkelItem key={item.versie} versie={item} />
          ))}
        </ol>
      </details>
    </div>
  );
}
